import { useState } from "react";
import { toast } from "sonner";
import { X, Mail, Lock, AtSign, UserRound } from "lucide-react";
import { COUNTRIES } from "@/data/countries";
import { useAuth } from "@/lib/auth";
import { detectCountryCode } from "@/lib/detectCountry";
import CountryPicker from "@/components/CountryPicker";
import LegalNoticeModal from "@/components/LegalNoticeModal";

const COLORS = ["#00D9FF", "#FF006E", "#9D4EDD", "#00FF66", "#F59E0B", "#3B82F6", "#14B8A6", "#F43F5E"];
const EMOJIS = ["🎬", "🍿", "😎", "🚀", "👾", "🦊", "🐼", "🔥", "🌙", "⚡", "🎧", "👑"];

export default function AuthModal({ open, onClose }) {
  const { login, signup, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [countryCode, setCountryCode] = useState(() => detectCountryCode() || "");
  const [color, setColor] = useState(COLORS[0]);
  const [emoji, setEmoji] = useState(EMOJIS[0]);
  const [busy, setBusy] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [pendingCountry, setPendingCountry] = useState(null);

  if (!open) return null;

  const submit = async () => {
    if (mode === "login") {
      setBusy(true);
      try {
        await login(email.trim(), password);
        toast.success("Welcome back!");
        onClose();
      } catch (e) {
        toast.error(e.message || "Something went wrong");
      } finally { setBusy(false); }
      return;
    }
    const c = COUNTRIES.find((x) => x.code === countryCode);
    if (!c) { toast.error("Pick your country"); return; }
    if (!email.trim() || !username.trim() || password.length < 6) {
      toast.error("Enter a valid email, username and a 6+ character password.");
      return;
    }
    setPendingCountry(c);
    setLegalOpen(true);
  };

  const completeSignup = async () => {
    const c = pendingCountry;
    if (!c) return;
    setBusy(true);
    try {
      await signup({
        email: email.trim(), username: username.trim(), password,
        country: c.name, countryCode: c.code, flag: c.flag, language: c.language,
        avatarColor: color, avatarEmoji: emoji,
        tosAccepted: true, tosAcceptedAt: new Date().toISOString(),
        tosLanguage: c.language, tosCountry: c.name,
      });
      toast.success("Account created! 🎉");
      setLegalOpen(false);
      onClose();
    } catch (e) {
      toast.error(e.message || "Something went wrong");
    } finally { setBusy(false); }
  };

  const field = "input-neon w-full h-11 bg-[#060817]/80 border border-[#00D9FF]/25 rounded-xl pl-10 pr-3 text-[#F0F6FF] outline-none text-sm transition-colors placeholder:text-[#88A0C0]";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="auth-modal">
      <div className="absolute inset-0 bg-[#04060F]/80 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-md glass-cyber rounded-3xl p-6 anim-in">
        <button data-testid="auth-close" onClick={onClose} className="absolute top-4 right-4 text-[#88A0C0] hover:text-[#00D9FF] transition-colors"><X className="w-5 h-5" /></button>
        <div className="flex gap-2 mb-5 bg-[#060817]/80 border border-white/5 rounded-full p-1">
          {["login", "signup"].map((m) => (
            <button key={m} data-testid={`auth-tab-${m}`} onClick={() => setMode(m)}
              className={`btn-neon flex-1 h-9 rounded-full text-sm font-display font-bold uppercase tracking-wider transition-all ${
                mode === m
                  ? (m === "login" ? "bg-[#00D9FF]/15 border border-[#00D9FF] text-[#00D9FF] glow-cyan" : "bg-[#FF006E]/15 border border-[#FF006E] text-[#FF006E] glow-pink")
                  : "text-[#88A0C0]"
              }`}>
              {m === "login" ? "Log in" : "Sign up"}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          <button
            type="button"
            data-testid="google-signin-button"
            onClick={loginWithGoogle}
            className="btn-neon w-full h-11 rounded-full bg-white text-[#1a1a2e] font-semibold flex items-center justify-center gap-2.5 hover:bg-white/90"
          >
            <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 4.5 29.4 2.5 24 2.5 12.1 2.5 2.5 12.1 2.5 24S12.1 45.5 24 45.5 45.5 35.9 45.5 24c0-1.2-.1-2.3-.3-3.5z"/>
              <path fill="#FF3D00" d="M5.3 14.7l6.6 4.8C13.7 15.1 18.5 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6 29.4 4 24 4 16 4 9.1 8.5 5.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.3C29.3 34.9 26.8 36 24 36c-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C9.9 39.5 16.4 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l6.3 5.3C41.9 35.6 45.5 30.3 45.5 24c0-1.2-.1-2.3-.3-3.5z"/>
            </svg>
            Continue with Google
          </button>
          <div className="flex items-center gap-3 py-0.5">
            <span className="flex-1 h-px bg-gradient-to-r from-transparent to-white/15" />
            <span className="text-[11px] uppercase tracking-widest text-[#88A0C0] font-display">or {mode === "login" ? "log in" : "sign up"} with email</span>
            <span className="flex-1 h-px bg-gradient-to-l from-transparent to-white/15" />
          </div>

          <div className="relative anim-in" style={{ animationDelay: "0.05s" }}>
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00D9FF]" />
            <input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={field} />
          </div>

          {mode === "signup" && (
            <div className="relative anim-in" style={{ animationDelay: "0.1s" }}>
              <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00D9FF]" />
              <input data-testid="auth-username" value={username} onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))} placeholder="Your name" className={field} />
            </div>
          )}

          <div className="relative anim-in" style={{ animationDelay: "0.15s" }}>
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00D9FF]" />
            <input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (6+ chars)" className={field} onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />
          </div>

          {mode === "signup" && (
            <>
              <CountryPicker
                value={countryCode}
                onChange={setCountryCode}
                triggerTestId="auth-country"
                optionPrefix="auth-country-option"
                searchTestId="auth-country-search"
                placeholder="Your country"
              />

              <div>
                <p className="text-[10px] uppercase tracking-widest neon-cyan font-display mb-1.5">Avatar</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl shrink-0" style={{ backgroundColor: color, boxShadow: `0 0 16px ${color}99` }} data-testid="auth-avatar-preview">{emoji}</div>
                  <div className="flex-1 space-y-1.5">
                    <div className="flex gap-1.5 flex-wrap">
                      {COLORS.map((c) => <button key={c} onClick={() => setColor(c)} className={`w-5 h-5 rounded-full transition-transform hover:scale-110 ${color === c ? "ring-2 ring-white" : ""}`} style={{ backgroundColor: c, boxShadow: color === c ? `0 0 10px ${c}` : "none" }} />)}
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {EMOJIS.map((e) => <button key={e} onClick={() => setEmoji(e)} className={`w-6 h-6 rounded text-sm transition-colors ${emoji === e ? "bg-[#00D9FF]/25" : "hover:bg-white/10"}`}>{e}</button>)}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          <button data-testid="auth-submit" onClick={submit} disabled={busy}
            className={`btn-neon w-full h-11 font-display font-bold uppercase tracking-wider rounded-full disabled:opacity-60 flex items-center justify-center ${
              mode === "login" ? "bg-[#00D9FF] text-navy-ink glow-cyan" : "bg-[#FF006E] text-white glow-pink"
            }`}>
            {busy ? "…" : mode === "login" ? "Log in" : "Create account"}
          </button>

          <div className="flex items-center gap-3 py-1">
            <span className="flex-1 h-px bg-gradient-to-r from-transparent to-[#9D4EDD]/50" />
            <span className="text-[11px] uppercase tracking-widest neon-purple font-display">or</span>
            <span className="flex-1 h-px bg-gradient-to-l from-transparent to-[#9D4EDD]/50" />
          </div>

          <button
            data-testid="continue-as-guest-button"
            onClick={() => { onClose(); toast("Just enter your name & country, then Create a Room 🍿"); }}
            className="btn-neon glow-purple w-full h-11 bg-[#9D4EDD]/12 border border-[#9D4EDD] text-[#9D4EDD] font-display font-bold uppercase tracking-wider rounded-full flex items-center justify-center gap-2"
          >
            <UserRound className="w-4 h-4" /> Continue as Guest
          </button>
          <p className="text-center text-[11px] text-[#88A0C0] -mt-1">No email needed — just a name and country to start watching.</p>
        </div>
      </div>
      <LegalNoticeModal
        open={legalOpen}
        language={pendingCountry?.language || "English"}
        busy={busy}
        onAgree={completeSignup}
        onDecline={() => { setLegalOpen(false); toast("You must accept the Terms of Service to create an account."); }}
      />
    </div>
  );
}
